import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, ClipboardEdit, ChevronLeft } from 'lucide-react';
import { publicFormsApi, getErrorMessage } from '../services/api';
import { FormRenderer } from '../components/forms/FormRenderer';

/**
 * Public candidate-facing form completion page.
 *
 * URL: `/forms/complete/:token`
 *
 * Behaviour:
 *   - Token-based access (no auth).
 *   - Hits `/api/public/forms/access/{token}` on mount to resolve the
 *     submission + form definition + recruiter branding.
 *   - Submits via `/api/public/forms/submit/{token}` on completion.
 *   - Shows a branded success card on completion.
 *
 * Isolated from `CandidateCompletionPage` so a regression in the PDF
 * candidate flow cannot break online forms (and vice-versa).
 */
export default function FormCompletionPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, submission: null, form: null, branding: {}, portalToken: null });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicFormsApi.open(token);
        setState({
          loading: false,
          error: null,
          submission: data.submission,
          form: data.form,
          branding: data.branding || {},
          portalToken: data.portal_token || null,
        });
        if (data.submission?.status === 'completed') {
          setDone(true);
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        setState({
          loading: false,
          error: typeof detail === 'string' ? detail : 'This link is invalid or has expired.',
          submission: null,
          form: null,
          branding: {},
          portalToken: null,
        });
      }
    })();
  }, [token]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await publicFormsApi.submit(token, {
        field_values: values,
        uploads: {},
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Submission failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const branding = state.branding || {};
  const form = state.form || {};
  // Per-form accent color overrides the tenant brand color so recruiters
  // can give a single form a bespoke look without changing org branding.
  const primaryColor = form.accent_color || branding.primary_color || '#f97316';
  const secondaryColor = branding.secondary_color || primaryColor;
  const companyName = branding.company_name || 'HyreDocs';
  const logoUrl = branding.company_logo_url;
  const coverImage = form.cover_image_url;

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-muted-foreground">Loading form…</div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full rounded-2xl bg-white border border-rose-100 p-8 text-center" data-testid="form-completion-error">
          <AlertTriangle className="w-12 h-12 mx-auto text-rose-500 mb-3" />
          <h1 className="font-heading text-xl text-foreground mb-2">Form unavailable</h1>
          <p className="text-sm text-muted-foreground">{state.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Branded header */}
      <header
        className="relative overflow-hidden text-white px-6 py-10"
        style={
          coverImage
            ? {
                backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.55)), url(${coverImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : {
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
              }
        }
        data-testid="form-completion-header"
      >
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          {logoUrl ? (
            <img src={logoUrl} alt={companyName} className="h-10 w-auto bg-white/10 rounded-lg p-1" />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-white/15 flex items-center justify-center">
              <ClipboardEdit className="w-5 h-5" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider opacity-80">{companyName}</p>
            <h1 className="font-heading text-2xl mt-0.5 truncate">{state.form.name}</h1>
          </div>
        </div>
      </header>

      {/* Back to portal sub-banner */}
      {(state.portalToken || state.submission?.candidate_email) && (
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2">
          <div className="max-w-3xl mx-auto flex items-center justify-end">
            <a
              href={state.portalToken
                ? `/portal/access/${state.portalToken}`
                : `/portal?email=${encodeURIComponent(state.submission?.candidate_email || '')}`}
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 hover:underline"
              data-testid="back-to-portal-link"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to All Documents
            </a>
          </div>
        </div>
      )}

      {/* Form / success */}
      <main className="max-w-3xl mx-auto px-6 py-8">
        {done ? (
          <div className="rounded-2xl bg-white border border-emerald-200 p-10 text-center" data-testid="form-completion-success">
            <div
              className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <CheckCircle2 className="w-7 h-7" style={{ color: primaryColor }} />
            </div>
            <h2 className="font-heading text-2xl text-foreground mb-2">Thank you!</h2>
            <p className="text-base text-muted-foreground whitespace-pre-wrap">
              {state.form.completion_message || 'Your form has been submitted.'}
            </p>
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-border p-6 sm:p-8">
            <p className="text-sm text-muted-foreground mb-4">
              Hi <strong>{state.submission.candidate_name}</strong>, please complete the form below.
            </p>
            <FormRenderer
              form={state.form}
              initialValues={state.submission.field_values || []}
              onSubmit={handleSubmit}
              submitting={submitting}
              primaryColor={primaryColor}
            />
          </div>
        )}
      </main>

      <footer className="text-center py-6 text-xs text-muted-foreground">
        Powered by <strong>HyreDocs</strong>
      </footer>
    </div>
  );
}
